/**
 * Appointment display helpers for the patient-facing booking flow (UK).
 *
 * Duration source of truth: the API value (MeetingType.yoman_interval, surfaced
 * as `duration_minutes` on the schedule context). The spec's fixed table is used
 * only as a fallback when the API does not provide a usable duration.
 */

// Spec fallback durations (minutes), matched against the appointment type name.
// Order matters: more specific patterns first.
const FALLBACK_DURATIONS_BY_NAME: Array<{ match: RegExp; minutes: number }> = [
  { match: /titration\s*follow.?up/i, minutes: 15 },
  { match: /titration\s*intake/i, minutes: 30 },
  { match: /care\s*manager\s*intake/i, minutes: 60 },
  { match: /(psychiatrist|psy)\s*intake/i, minutes: 60 },
];

/**
 * Resolve an appointment's duration in minutes.
 * Prefers the API-provided duration; falls back to the spec table by type name.
 * Returns null when neither source yields a usable value.
 */
export const resolveDurationMinutes = (
  apiDuration?: number | null,
  typeName?: string | null,
): number | null => {
  if (typeof apiDuration === 'number' && apiDuration > 0) return apiDuration;
  if (typeName) {
    const hit = FALLBACK_DURATIONS_BY_NAME.find((d) => d.match.test(typeName));
    if (hit) return hit.minutes;
  }
  return null;
};

/** Human-readable duration, e.g. "60 Minutes". Empty string when unknown. */
export const formatDuration = (minutes: number | null): string => {
  if (!minutes || minutes <= 0) return '';
  return `${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
};

/** Convert a 24h "HH:MM" time string to 12h with meridiem, e.g. "13:30" → "01:30 PM". */
export const formatSlotTime = (hhmm: string): string => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return hhmm;
  let hour = Number(m[1]);
  const minute = m[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${String(hour).padStart(2, '0')}:${minute} ${period}`;
};
